import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

// Use the same locally-bundled SDK that sesami-booking.js uses — avoids a second
// CDN network request and ensures both files share the same SesamiSDK version.
const SDK_SRC = new URL('sesami-sdk.js', import.meta.url).href;

class BookingContactFormComponent extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.#setBookingStep(isMobileBreakpoint() ? 5 : 4);

    const form = this.querySelector('.contact-form__form');
    if (form) {
      form.addEventListener('submit', this.#handleSubmit, { signal: this.#abort.signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  // ─── Form submission ─────────────────────────────────────────────────────────

  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement} */ (event.target);
    const submitBtn = form.querySelector('[type="submit"]');
    const errorEl = this.#getOrCreateErrorEl(form);

    errorEl.textContent = '';
    if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = true;

    try {
      const customer = this.#buildCustomer(form);
      const privacyAccepted = this.#readPrivacyConsent(form);
      const cart = await this.#fetchCart();

      const appointmentProductId = Number(this.dataset.appointmentProductId || 0);
      const appointmentItem = Array.isArray(cart.items)
        ? cart.items.find((item) => item.product_id === appointmentProductId)
        : null;

      if (!appointmentItem) {
        throw new Error('No appointment slot found in your cart. Please go back and select a date.');
      }

      const slot = this.#buildSlot(appointmentItem);
      const options = this.#buildOptions(cart.items, appointmentProductId, appointmentItem, privacyAccepted);

      const sesami = await this.#ensureSesami(appointmentItem);

      // Debug: log what we're sending so we can verify the token and slot are fresh
      console.group('BookingContactForm: book() payload');
      console.log('slot:', slot);
      console.log('customer:', { ...customer, email: customer.email ? '***' : '(empty)' });
      console.log('privacyAccepted:', privacyAccepted);
      console.log('options:', JSON.stringify(options, null, 2));
      console.log('sesami.accesstoken present:', Boolean(sesami.accesstoken));
      console.groupEnd();

      await sesami.book(slot, customer, options);

      this.#onSuccess();
    } catch (error) {
      console.error('BookingContactForm: booking failed', error);

      // Sesami returns the raw Response object on failure — extract a useful status
      let message = error.message || 'Something went wrong. Please try again.';

      if (error && typeof error.status === 'number') {
        if (error.status === 410) {
          message = 'Error 410. Your time slot reservation has expired.';
        } else if (error.status === 401) {
          message = 'Error 401. Authorisation failed. Please refresh the page and try again.';
        } else if (error.status === 409) {
          message = 'Error 409. This time slot is no longer available.';
        }
      }

      errorEl.textContent = message;
      if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = false;
    }
  };

  // ─── Data builders ────────────────────────────────────────────────────────────

  /**
   * Reads first name, last name, email and phone from the form.
   * @param {HTMLFormElement} form
   */
  #buildCustomer(form) {
    const firstName = /** @type {HTMLInputElement | null} */ (
      form.querySelector('#ContactForm-first-name')?.value || ''
    ).trim();
    const lastName = /** @type {HTMLInputElement | null} */ (
      form.querySelector('#ContactForm-last-name')?.value || ''
    ).trim();
    const email = /** @type {HTMLInputElement | null} */ (form.querySelector('#ContactForm-email')?.value || '').trim();
    const phone = /** @type {HTMLInputElement | null} */ (form.querySelector('#ContactForm-phone')?.value || '').trim();

    if (!email) throw new Error('Email is required.');

    // E.164: starts with +, then 7–15 digits (phone is optional but must be valid if provided)
    if (phone && !/^\+[1-9]\d{6,14}$/.test(phone)) {
      throw new Error('Please enter a valid phone number in international format, e.g. +34612345678');
    }

    return { email, firstName, lastName, phone };
  }

  /**
   * Reads the privacy checkbox. Kept out of #buildCustomer because
   * SesamiCustomerObject only accepts email, firstName, lastName and phone.
   *
   * A missing checkbox is a template bug, not a "no": returning false there
   * would record a consent that nobody was ever asked for, with no error
   * anywhere. So it throws instead.
   *
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  #readPrivacyConsent(form) {
    const privacyCheckbox = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-privacy-checkbox]'));

    if (!privacyCheckbox) {
      console.error('BookingContactForm: [data-privacy-checkbox] not found in the form');
      throw new Error('This form is misconfigured and cannot be submitted. Please contact us.');
    }

    return privacyCheckbox.checked;
  }

  /**
   * Reconstructs a minimal SesamiSlotObject from the cart appointment line properties.
   * @param {any} appointmentItem - Cart line item for the appointment product.
   */
  #buildSlot(appointmentItem) {
    const props = appointmentItem.properties || {};
    const bookingDate = (props['Date'] || '').trim();
    const bookingTime = (props['Time'] || '').trim();

    if (!bookingDate || !bookingTime) {
      throw new Error('Could not find booking date/time in your cart. Please go back and select a slot.');
    }

    return {
      startTime: `${bookingDate} ${bookingTime}`, // "YYYY-MM-DD HH:mm"
      duration: 60,
      status: 'available',
      remainingSlots: Number(appointmentItem.properties?.['remainingSlots']) || 1,
    };
  }

  /**
   * Builds SesamiOptionsObject in the exact format the SDK expects:
   * { properties: [{ name: string, value: string }] }
   *
   * Each non-appointment cart item becomes one property entry.
   * The reservation token is appended so Sesami can tie this book() call
   * to the existing slot hold.
   *
   * @param {any[]} items
   * @param {number} appointmentProductId
   * @param {any} appointmentItem
   * @param {boolean} privacyAccepted
   * @returns {{ properties: { name: string, value: string }[] }}
   */
  #buildOptions(items, appointmentProductId, appointmentItem = null, privacyAccepted = false) {
    const MAX_PROPERTIES = 6;
    const excludedProductHandles = this.#getExcludedProductHandles();

    // Keep only cart lines that are not excluded by booking settings.
    const lines = items.filter((item) => {
      const productId = Number(item?.product_id ?? 0);
      const productHandle = String(item?.handle ?? '')
        .trim()
        .toLowerCase();
      const itemUrl = String(item?.url ?? '');
      const isFreeGiftAppItem = itemUrl.includes('sca_clone_freegift');
      const isExcludedHandle = productHandle && excludedProductHandles.has(productHandle);
      const isAppointmentProduct = appointmentProductId && productId === appointmentProductId;

      return productId && !isExcludedHandle && !isAppointmentProduct && !isFreeGiftAppItem;
    });

    // Truncate to the first MAX_PROPERTIES items
    const selected = lines.slice(0, MAX_PROPERTIES);

    const properties = selected.map((item, index) => {
      const title = (item?.title || `Product ${item?.product_id ?? ''}`).toString().trim();
      const variantId = item?.variant_id ?? '';
      // Format value as "title: variant_id"
      const value = `${title}: ${variantId} | privacy: ${privacyAccepted ? 'yes' : 'no'}`;
      return {
        name: `product_${index + 1}`,
        value,
      };
    });

    // If appointment item available, read the reservation token from its line properties
    try {
      const token = (appointmentItem && appointmentItem.properties && (appointmentItem.properties._reservation_token || appointmentItem.properties['_reservation_token'])) || null;
      if (token) {
        // Ensure the reservation token is included so Sesami can match the reservation in book()
        // Prepend so server sees token first (order typically doesn't matter, but it's clearer)
        properties.unshift({ name: '_reservation_token', value: String(token) });
      }
    } catch (e) {
      // ignore - we simply won't include the token if we can't read it
      console.warn('BookingContactForm: could not read reservation token from appointment item', e);
    }

    return { properties };
  }

  /**
   * Reads booking-mode excluded product handles from the component dataset.
   * Accepts comma/newline-separated values and strips extra spaces.
   * @returns {Set<string>}
   */
  #getExcludedProductHandles() {
    const raw = (this.dataset.bookingExcludedProductHandles || '').replace(/[\n\r]/g, ',');
    const ids = raw
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);

    return new Set(ids);
  }

  // ─── SDK initialisation ───────────────────────────────────────────────────────

  /**
   * Returns window.sesami if already initialised, otherwise boots a new instance.
   * @param {any} appointmentItem
   */
  async #ensureSesami(appointmentItem) {
    if (window.sesami && typeof window.sesami.book === 'function') {
      return window.sesami;
    }

    await this.#loadSDK();

    const SesamiCtor = window.SesamiSDK || window.Sesami;
    if (typeof SesamiCtor !== 'function') {
      throw new Error('Sesami SDK could not be loaded. Please refresh and try again.');
    }

    // Reuse the token persisted by sesami-booking.js during reserve().
    // Sesami tokens are URL-scoped: fetching a new token on the confirmation page
    // (different URL) causes a 410 on book() because Sesami can't match the reservation.
    let storedToken = null;
    try {
      storedToken = sessionStorage.getItem('sesami_access_token') || null;
    } catch (e) {
      // sessionStorage unavailable — fall back to fetching a fresh token
    }

    const sesami = new SesamiCtor({
      shopId: String(this.dataset.shopId || ''),
      productId: String(appointmentItem.product_id),
      variantId: String(appointmentItem.variant_id),
      quantity: 1,
      autoLoad: false,
      locale: this.dataset.locale || document.documentElement.lang || 'en',
      accesstoken: storedToken || undefined,
    });

    await sesami.init();

    // Only fetch a fresh token if we don't have the original one.
    // Fetching a new token on a different page URL returns a URL-scoped token
    // that cannot fulfil reservations made on the booking product page.
    if (!sesami.accesstoken) {
      await sesami.getToken();
    }

    window.sesami = sesami;
    return sesami;
  }

  /** Injects the Sesami SDK script if not already present. */
  #loadSDK() {
    return new Promise((resolve, reject) => {
      if (window.SesamiSDK || window.Sesami) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = SDK_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Sesami SDK.'));
      document.head.appendChild(script);
    });
  }

  // ─── UI transitions ───────────────────────────────────────────────────────────

  #onSuccess() {
    const formBlock = this.querySelector('[data-booking-form-block]');
    const formBlockCopy = document.querySelector('.contact-form-copy');
    const mainTitle = document.querySelector('.text-block.h1');
    const successBlock = document.querySelector('.booking-confirmed');

    if (formBlock instanceof HTMLElement) formBlock.hidden = true;
    if (formBlockCopy instanceof HTMLElement) formBlockCopy.style.display = 'none';
    if (mainTitle instanceof HTMLElement) mainTitle.style.display = 'none';

    // add date inside the success block for reminder
    const date = document.querySelector('.cart-drawer__content .booking-date');
    if (date && successBlock instanceof HTMLElement) {
      const existingDate = successBlock.querySelector('.booking-date-success');
      if (existingDate) existingDate.remove();
      const dateClone = date.cloneNode(true);
      dateClone.classList.add('booking-date-success');
      successBlock.prepend(dateClone);
    }

    if (successBlock instanceof HTMLElement) successBlock.style.display = 'block';

    // Clean up the persisted token — the booking is complete
    try {
      sessionStorage.removeItem('sesami_access_token');
    } catch (e) {
      /* ignore */
    }

    this.#removeItemsFromCart().catch((error) => {
      console.warn('BookingContactForm: cart cleanup failed', error);
    });
    this.#setBookingStep(6);
  }

  /**
   * Clears the Shopify cart and removes the booking state class from the body.
   * Called after a successful sesami.book() so the user starts fresh.
   */
  async #removeItemsFromCart() {
    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });

    // Fetch the now-empty cart so we can broadcast the update to all cart components
    // (cart bubble, cart items list, cart drawer heading, etc.)
    let emptyCart = { items: [], item_count: 0, total_price: 0 };
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (res.ok) emptyCart = await res.json();
    } catch (e) {
      console.warn('BookingContactForm: could not fetch cart after clear', e);
    }

    // Also clear the persisted Sesami token (belt-and-suspenders in case #onSuccess didn't)
    try {
      sessionStorage.removeItem('sesami_access_token');
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * Advances the connected announcement bar to the given step index.
   * Targets only the bar that has data-booking-step-title so regular
   * announcement bars on other pages are not affected.
   * @param {number} stepIndex
   */

  #setBookingStep(stepIndex) {
    const bar = document.querySelector('[data-booking-step-title] announcement-bar-component');
    if (bar && typeof bar.current !== 'undefined') {
      bar.current = stepIndex;
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  #getOrCreateErrorEl(form) {
    let el = form.querySelector('.booking-contact-form__error');
    if (!el) {
      el = document.createElement('div');
      el.className = 'booking-contact-form__error contact-form__error';
      el.setAttribute('role', 'alert');
      form.appendChild(el);
    }
    return el;
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
    return response.json();
  }
}

if (!customElements.get('booking-contact-form-component')) {
  customElements.define('booking-contact-form-component', BookingContactFormComponent);
}
